import { Box, Chip, CircularProgress, Paper, Typography } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonIcon from "@mui/icons-material/Person";
import { format } from "date-fns";

import { ResidentPeriod } from "../../../entities/admin-communities";
import { tokens } from "../../../shared/config/theme";

const formatDateTime = (value: string) =>
  format(new Date(value), "dd.MM.yyyy HH:mm");

const getPeriodLifecycle = (period: ResidentPeriod) => {
  const now = Date.now();
  if (now < new Date(period.beginAt).getTime()) {
    return { label: "Будущий", color: "info" as const };
  }
  if (now > new Date(period.endAt).getTime()) {
    return { label: "Завершён", color: "default" as const };
  }
  return { label: "Активный", color: "success" as const };
};

const getSubscriptionStatusCaption = (
  status: NonNullable<ResidentPeriod["subscribtion"]>["status"],
) => {
  switch (status) {
    case "canceled":
      return "Подписка отменена";
    case "expired":
      return "Подписка истекла";
    case "pending":
      return "Ожидает оплаты";
    default:
      return null;
  }
};

const getPeriodTypeMeta = (type: ResidentPeriod["type"]) => {
  switch (type) {
    case "subscription":
      return { label: "Подписка", color: "primary" as const, accent: tokens.accent.main };
    case "manual":
      return { label: "Без оплаты", color: "warning" as const, accent: tokens.accent.main };
    case "externalPayment":
      return { label: "Внешняя оплата", color: "info" as const, accent: tokens.semantic.info };
    case "referalBonus":
      return { label: "Реферальный бонус", color: "success" as const, accent: tokens.semantic.success };
    default:
      return { label: "Период", color: "default" as const, accent: tokens.accent.main };
  }
};

const PeriodDetailBlock = ({
  title,
  person,
  note,
  caption,
}: {
  title: string;
  person?: string;
  note?: string;
  caption: string;
}) => (
  <Box
    sx={{
      mt: 2,
      p: 1.75,
      borderRadius: 1.5,
      bgcolor: tokens.navy[900],
      border: "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
      {title}
    </Typography>
    {person && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <PersonIcon fontSize="small" sx={{ color: "text.secondary" }} />
        <Typography variant="body2" fontWeight={600}>
          {person}
        </Typography>
      </Box>
    )}
    {note && (
      <Typography variant="body2" sx={{ mt: 0.75 }}>
        {note}
      </Typography>
    )}
    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
      {caption}
    </Typography>
  </Box>
);

interface ResidentPeriodsViewProps {
  isFetching: boolean;
  periods?: ResidentPeriod[];
  emptyHint?: string;
}

export const ResidentPeriodsView = ({
  isFetching,
  periods,
  emptyHint = "У данного пользователя еще нет записей о периодах резиденства",
}: ResidentPeriodsViewProps) => {
  if (isFetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!periods?.length) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Нет данных о периодах резиденства
        </Typography>
        <Typography variant="body2" color="text.disabled">
          {emptyHint}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
      {[...periods].reverse().map((period) => {
        const typeMeta = getPeriodTypeMeta(period.type);
        const lifecycle = getPeriodLifecycle(period);

        return (
          <Paper
            key={period.id}
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderLeft: "3px solid",
              borderLeftColor: typeMeta.accent,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: 1.75,
              }}
            >
              <Chip label={typeMeta.label} color={typeMeta.color} size="small" />
              <Chip
                label={lifecycle.label}
                size="small"
                color={lifecycle.color}
                variant={lifecycle.color === "default" ? "outlined" : "filled"}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
              <CalendarMonthIcon
                fontSize="small"
                sx={{ color: "text.secondary", mt: 0.25 }}
              />
              <Box>
                <Typography variant="subtitle1" fontWeight={600} lineHeight={1.35}>
                  {formatDateTime(period.beginAt)} — {formatDateTime(period.endAt)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Создано {formatDateTime(period.createdAt)}
                </Typography>
              </Box>
            </Box>

            {period.subscribtion && (
              <PeriodDetailBlock
                title="Подписка"
                note={`${period.subscribtion.planTitle} · ${period.subscribtion.price.value} ${period.subscribtion.price.unicode} · ${lifecycle.label}`}
                caption={
                  [
                    period.subscribtion.firstPaymentAt
                      ? `Первый платёж ${formatDateTime(period.subscribtion.firstPaymentAt)}`
                      : null,
                    period.subscribtion.nextPaymentAt
                      ? `Следующий платёж ${formatDateTime(period.subscribtion.nextPaymentAt)}`
                      : null,
                    getSubscriptionStatusCaption(period.subscribtion.status),
                  ]
                    .filter(Boolean)
                    .join(" · ") || "Период создан по оплаченной подписке"
                }
              />
            )}

            {period.manual && (
              <PeriodDetailBlock
                title="Назначил администратор"
                person={`${period.manual.firstName} ${period.manual.lastName}`.trim()}
                note={period.manual.description}
                caption="Техническое ручное назначение без учёта оплаты и бонусов подписки"
              />
            )}

            {period.externalPayment && (
              <PeriodDetailBlock
                title="Внешняя оплата — назначил администратор"
                person={`${period.externalPayment.firstName} ${period.externalPayment.lastName}`.trim()}
                note={period.externalPayment.description}
                caption="Период создан с учётом правил оплаты (бонусные дни и т.п.)"
              />
            )}

            {period.referalBonus && (
              <PeriodDetailBlock
                title="Реферальный бонус"
                person={`${period.referalBonus.firstName} ${period.referalBonus.lastName}`.trim()}
                caption="Резидентство получено за приглашение пользователя, который оплатил подписку"
              />
            )}
          </Paper>
        );
      })}
    </Box>
  );
};
